import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PostDraftInterface } from '../interfaces/post.interface';

export interface ReplyDraftsState {
  drafts: PostDraftInterface[];
  activeDraftId: string | null;
  loading: boolean;
  feedPostId: string | undefined;
  isAddingReplyDraft: boolean;
  publishInProgress: boolean;
  publishSuccess: boolean;
}

const initialState: ReplyDraftsState = {
  drafts: [],
  activeDraftId: null,
  loading: false,
  feedPostId: undefined,
  isAddingReplyDraft: false,
  publishInProgress: false,
  publishSuccess: false,
};

export const replyDraftsSlice = createSlice({
  name: 'replyDraftsStore',
  initialState,
  reducers: {
    SET_REPLY_DRAFTS: (state, action: PayloadAction<PostDraftInterface[]>) => {
      state.drafts = action.payload;
      state.loading = false;
    },
    ADD_REPLY_DRAFT: (state, action: PayloadAction<PostDraftInterface>) => {
      state.drafts.push(action.payload);
    },
    REMOVE_REPLY_DRAFT: (state, action: PayloadAction<string>) => {
      state.drafts = state.drafts.filter((d) => d.id !== action.payload);
    },
    UPDATE_REPLY_DRAFT: (
      state,
      action: PayloadAction<{
        draftId: string;
        updates: Partial<PostDraftInterface>;
      }>,
    ) => {
      const index = state.drafts.findIndex(
        (d) => d.id === action.payload.draftId,
      );
      if (index !== -1) {
        state.drafts[index] = {
          ...state.drafts[index],
          ...action.payload.updates,
        };
      }
    },
    SET_ACTIVE_DRAFT_ID: (state, action: PayloadAction<string | null>) => {
      state.activeDraftId = action.payload;
    },
    SET_REPLY_DRAFTS_LOADING: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    SET_FEED_POST_ID: (state, action: PayloadAction<string | undefined>) => {
      state.feedPostId = action.payload;
    },
    SET_IS_ADDING_REPLY_DRAFT: (state, action: PayloadAction<boolean>) => {
      state.isAddingReplyDraft = action.payload;
    },
    SET_PUBLISH_IN_PROGRESS: (state, action: PayloadAction<boolean>) => {
      state.publishInProgress = action.payload;
    },
    SET_PUBLISH_SUCCESS: (state, action: PayloadAction<boolean>) => {
      state.publishSuccess = action.payload;
    },
    RESET_REPLY_DRAFTS: () => initialState,
  },
});

export const {
  SET_REPLY_DRAFTS,
  ADD_REPLY_DRAFT,
  REMOVE_REPLY_DRAFT,
  UPDATE_REPLY_DRAFT,
  SET_ACTIVE_DRAFT_ID,
  SET_REPLY_DRAFTS_LOADING,
  SET_FEED_POST_ID,
  SET_IS_ADDING_REPLY_DRAFT,
  SET_PUBLISH_IN_PROGRESS,
  SET_PUBLISH_SUCCESS,
  RESET_REPLY_DRAFTS,
} = replyDraftsSlice.actions;

export default replyDraftsSlice.reducer;
